import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import { DocumentReference } from '@angular/fire/firestore';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AppRoutes } from '@app/app.routes';
import { IInventoryItemCreateForm } from '@inventory/models/inventory-forms';
import { InventoryRoutes } from '@inventory/pages/inventory.routes';
import { InventoryItemsService } from '@inventory/services/inventory-items/inventory-items.service';
import { DropdownControlComponent, IDropdownOption } from '@shared/components/forms/dropdown-control/dropdown-control.component';
import { StandardControlComponent } from '@shared/components/forms/standard-control/standard-control.component';
import { TextareaControlComponent } from '@shared/components/forms/textarea-control/textarea-control.component';
import { ToastTypes } from '@shared/models/toast';
import { OffcanvasService } from '@shared/services/offcanvas/offcanvas.service';
import { ToastService } from '@shared/services/toast/toast.service';

@Component({
	selector: 'app-inventory-item-create-offcanvas',
	imports: [CommonModule, StandardControlComponent, TextareaControlComponent, DropdownControlComponent],
	templateUrl: './inventory-item-create-offcanvas.component.html',
	styleUrl: './inventory-item-create-offcanvas.component.scss',
	host: {
		class: 'h-100',
		style: 'display: contents',
	},
})
export class InventoryItemCreateOffcanvasComponent implements OnInit {
	@Input() categories: Array<IDropdownOption> = [];
	@Input() organisationRef: DocumentReference;

	private inventoryItemsService = inject(InventoryItemsService);
	private offcanvasService = inject(OffcanvasService);
	private router = inject(Router);
	private toastService = inject(ToastService);

	form: FormGroup<IInventoryItemCreateForm>;

	isSubmitting: boolean = false;

	ngOnInit(): void {
		this.form = new FormGroup<IInventoryItemCreateForm>({
			name: new FormControl<string | null>(null, [Validators.required, Validators.minLength(2), Validators.maxLength(100)]),
			description: new FormControl<string | null>(null, [Validators.maxLength(500)]),
			sku: new FormControl<string | null>(null, [Validators.required, Validators.minLength(2), Validators.maxLength(50)]),
			category: new FormControl<string | null>(null),
			costPrice: new FormControl<number | null>(null, [Validators.min(0)]),
			sellPrice: new FormControl<number | null>(null, [Validators.min(0)]),
		});

		if (this.categories.length < 1) {
			this.form.controls.category.disable();
		}
	}

	close(): void {
		this.offcanvasService.dismissLast();
	}

	navigateToCreateCategory(): void {
		this.offcanvasService.dismissLast();
		this.router.navigate([AppRoutes.INVENTORY, InventoryRoutes.CATEGORIES]);
	}

	submit(): void {
		if (this.form.invalid) {
			return;
		}

		this.isSubmitting = true;

		this.inventoryItemsService
			.createInventoryItem(this.organisationRef, this.form)
			.then(() => {
				this.isSubmitting = false;
				this.offcanvasService.dismissLast();
			})
			.catch((error) => {
				console.error('Error creating inventory item:', error);
				this.isSubmitting = false;
				this.toastService.showToast({
					title: 'Inventory Item',
					message: `Failed to create inventory item: ${error.message}`,
					type: ToastTypes.ERROR,
				});
			});
	}
}
